import type { UserRow } from '@/features/configuracoes/api';
import { useAuthStore } from '@/shared/stores/auth-store';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { TicketsScope } from './api';
import { getSocket } from './socket';
import type { TicketStatus, TicketSummary } from './types';

/**
 * Nome de quem fez a ação, pros avisos de "o atendimento saiu da sua mão".
 *
 * O evento do servidor só traz o id. Quem encerra um pendente vira o dono
 * (COALESCE no /close), então o cartão já traz o nome; nos outros casos busca
 * na lista de usuários que o painel já carrega pro filtro e pra transferência
 * (`['settings-users']`). `null` = não deu pra saber → o texto fica genérico.
 */
export function nomeDoUsuario(
  qc: QueryClient,
  userId: number | null | undefined,
  card?: TicketSummary,
): string | null {
  if (userId == null) return null;
  if (card?.user_id === userId && card.user_name) return card.user_name;
  const users = qc.getQueryData<UserRow[]>(['settings-users']);
  return users?.find((u) => u.id === userId)?.name ?? null;
}

export type EventoDeSaida =
  | 'ticket.assigned'
  | 'ticket.transferred'
  | 'ticket.queued'
  | 'ticket.closed';

export interface EventoDoTicket {
  ticket_id?: number;
  ticket_ids?: number[];
  actor_user_id?: number | null;
  to_user_id?: number | null;
  ticket?: TicketSummary;
  tickets?: TicketSummary[];
}

// [singular, plural]
const VERBOS = {
  aceito: ['foi aceito', 'foram aceitos'],
  atribuido: ['foi atribuído', 'foram atribuídos'],
  transferido: ['foi transferido', 'foram transferidos'],
  fila: ['foi devolvido para a fila', 'foram devolvidos para a fila'],
  finalizado: ['foi finalizado', 'foram finalizados'],
} as const;

/** O card saiu da aba que o atendente está olhando? */
export function saiDaAba(
  evento: EventoDeSaida,
  evt: EventoDoTicket,
  card: TicketSummary | undefined,
  aba: { activeStatus: TicketStatus; scope: TicketsScope; meuId: number | undefined },
): boolean {
  const { activeStatus, scope, meuId } = aba;
  // Seleção só existe em Pendentes e Ativos — fechado sai das duas.
  if (evento === 'ticket.closed') return true;
  if (card) {
    if (card.status !== activeStatus) return true;
    return activeStatus === 'active' && scope === 'mine' && card.user_id !== meuId;
  }
  // Evento sem cartão: decide pela regra do evento.
  if (evento === 'ticket.queued') return activeStatus === 'active';
  if (activeStatus === 'pending') return typeof evt.to_user_id === 'number';
  return scope === 'mine' && evt.to_user_id !== meuId;
}

export function textoDoAviso(
  qc: QueryClient,
  evento: EventoDeSaida,
  evt: EventoDoTicket,
  ids: number[],
  cards: Map<number, TicketSummary>,
): string {
  const card = cards.get(ids[0] as number);
  const actor = evt.actor_user_id ?? null;
  let verbo: keyof typeof VERBOS;
  let complemento = '';
  if (evento === 'ticket.closed') {
    verbo = 'finalizado';
    const nome = nomeDoUsuario(qc, actor, card);
    complemento = actor == null ? ' automaticamente' : nome ? ` por ${nome}` : '';
  } else if (evento === 'ticket.queued') {
    verbo = 'fila';
  } else {
    const paraQuem = evt.to_user_id ?? null;
    const nome = nomeDoUsuario(qc, paraQuem, card);
    // Aceitar = a própria pessoa se atribui; o resto (distribuição automática,
    // admin atribuindo) é "atribuído a".
    verbo =
      evento === 'ticket.transferred' ? 'transferido' : actor === paraQuem ? 'aceito' : 'atribuido';
    if (nome)
      complemento =
        verbo === 'aceito'
          ? ` por ${nome}`
          : verbo === 'atribuido'
            ? ` a ${nome}`
            : ` para ${nome}`;
  }
  if (ids.length > 1)
    return `${ids.length} atendimentos selecionados ${VERBOS[verbo][1]}${complemento}.`;
  const rotulo = card?.protocol_number
    ? `Atendimento #${card.protocol_number}`
    : card?.contact.display_name
      ? `Atendimento de ${card.contact.display_name}`
      : 'Um atendimento selecionado';
  return `${rotulo} ${VERBOS[verbo][0]}${complemento}.`;
}

/**
 * Modo seleção: quando OUTRA pessoa aceita, transfere, devolve pra fila ou
 * encerra um dos selecionados, o card sai da lista — mas o id ficava no lote
 * ("3 de 2 selecionados") e ninguém avisava. Aqui o lote perde o id na hora,
 * com toast.
 *
 * Reage ao EVENTO, não a "sumiu da lista": a seleção que está fora do filtro
 * atual é mantida de propósito (vide "Desmarca todos os visíveis" no
 * InboxPanel), então sumir da tela não quer dizer que saiu da mão.
 */
export function useAvisoDaSelecao(opts: {
  /** Modo seleção ligado e com algo selecionado. */
  ativo: boolean;
  activeStatus: TicketStatus;
  scope: TicketsScope;
  selected: Set<number>;
  /** O que está na tela — acha o protocolo quando o evento não traz cartão. */
  lista: TicketSummary[];
  /** Conversa aberta: já recebe o aviso do useRealtimeListeners — sem toast em dobro. */
  abertoId: number | null;
  onPerdidos: (ids: number[]) => void;
}): void {
  const qc = useQueryClient();
  // Refs: os listeners ficam registrados enquanto `ativo`, sem re-registrar a
  // cada seleção/render.
  const ref = useRef(opts);
  ref.current = opts;

  useEffect(() => {
    if (!opts.ativo) return;
    const socket = getSocket();

    const tratar =
      (evento: EventoDeSaida) =>
      (evt?: EventoDoTicket): void => {
        if (!evt) return;
        const { selected, lista, abertoId, onPerdidos, activeStatus, scope } = ref.current;
        const meuId = useAuthStore.getState().user?.id;
        const ids = evt.ticket_ids ?? (typeof evt.ticket_id === 'number' ? [evt.ticket_id] : []);
        const cards = new Map<number, TicketSummary>();
        for (const c of evt.tickets ?? (evt.ticket ? [evt.ticket] : [])) cards.set(c.id, c);
        for (const t of lista) if (!cards.has(t.id)) cards.set(t.id, t);
        const perdidos = ids.filter(
          (id) =>
            selected.has(id) &&
            saiDaAba(
              evento,
              evt,
              evt.ticket?.id === id ? evt.ticket : evt.tickets?.find((c) => c.id === id),
              { activeStatus, scope, meuId },
            ),
        );
        if (perdidos.length === 0) return;
        onPerdidos(perdidos);
        // Fui eu (outra aba, ou o próprio lote desta tela): só tira do lote.
        if (meuId != null && evt.actor_user_id === meuId) return;
        const avisar = perdidos.filter((id) => id !== abertoId);
        if (avisar.length > 0) toast.info(textoDoAviso(qc, evento, evt, avisar, cards));
      };

    const onAssigned = tratar('ticket.assigned');
    const onTransferred = tratar('ticket.transferred');
    const onQueued = tratar('ticket.queued');
    const onClosed = tratar('ticket.closed');
    socket.on('ticket.assigned', onAssigned);
    socket.on('ticket.transferred', onTransferred);
    socket.on('ticket.queued', onQueued);
    socket.on('ticket.closed', onClosed);
    return () => {
      socket.off('ticket.assigned', onAssigned);
      socket.off('ticket.transferred', onTransferred);
      socket.off('ticket.queued', onQueued);
      socket.off('ticket.closed', onClosed);
    };
  }, [opts.ativo, qc]);
}
